/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ControlledDrawer, getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IDrawerState } from '@msdyn365-commerce-modules/utilities/dist/types/drawer/Drawer';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';

import { createAccordionStateInput } from '@msdyn365-commerce-modules/accordion';
import { IDobbiesAccordionItemData } from './dobbies-accordion-item.data';
import { IDobbiesAccordionItemProps } from './dobbies-accordion-item.props.autogenerated';
import { interactionStyle } from '../dobbies-accordion/dobbies-accordion.props.autogenerated';
import { autorun, reaction } from 'mobx';
import { _getScreenSize } from '../../themes/dobbies/utilities/display-utils';

export interface IAccordionItemViewProps {
    AccordionItemContainer: IModuleProps;
    drawer: INodeProps;
    accordionSlots: INodeProps;
    AccordionItemContent: INodeProps;
    className: string;
}

export interface IAccordionItemExtentedProps extends IDobbiesAccordionItemProps<IDobbiesAccordionItemData> {
    interactionStyle?: interactionStyle;
}

export interface IDobbiesAccordionItemState {
    isAccordionExpanded?: boolean;
    isAccordionSuccess?: boolean;
}

/**
 *
 * AccordionItem component.
 * @extends {React.Component<IAccordionItemExtentedProps>}
 */
@observer
class AccordionItem extends React.Component<IAccordionItemExtentedProps, IDobbiesAccordionItemState> {
    private readonly moduleClassName: string = 'ms-accordion-item';

    private readonly telemetryContent?: ITelemetryContent;

    constructor(props: IDobbiesAccordionItemProps<IDobbiesAccordionItemData>) {
        super(props);
        this._onToggle = this._onToggle.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.state = {
            isAccordionSuccess: false
        };
    }

    public async componentDidMount(): Promise<void> {
        reaction(
            () => {
                this.props.data.checkoutAccordionStep.result;
            },
            async () => {
                if (this.props.data.checkoutAccordionStep.result?.currentStep !== undefined) {
                    if (this.props.data.checkoutAccordionStep.result?.currentStep === this.props.config.accordionNumber) {
                        this.setState({ isAccordionExpanded: true });
                    } else {
                        // this.setState({ isAccordionExpanded: false });
                        if (
                            this.props.config.accordionNumber &&
                            this.props.data.checkoutAccordionStep.result?.currentStep > this.props.config.accordionNumber
                        ) {
                            this.setIsAccordionSuccess(true);
                        }
                    }
                }
            }
        );

        autorun(() => {
            if (this.props.data.checkoutAccordionStep.result?.currentStep !== undefined) {
                if (this.props.data.checkoutAccordionStep.result?.currentStep === this.props.config.accordionNumber) {
                    this.setState({ isAccordionExpanded: true });
                } else {
                    // this.setState({ isAccordionExpanded: false });
                    if (
                        this.props.config.accordionNumber &&
                        this.props.data.checkoutAccordionStep.result?.currentStep > this.props.config.accordionNumber
                    ) {
                        this.setState({ isAccordionExpanded: true });
                        this.setIsAccordionSuccess(true);
                    }
                }
            }
        });
    }

    public shouldComponentUpdate(nextProps: IDobbiesAccordionItemProps<IDobbiesAccordionItemData>): boolean {
        if (this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { slots, renderView, config, data } = this.props;
        const accordionExpandedStateResult = data && data.accordionExpandedState && data.accordionExpandedState.result;
        const expanded =
            accordionExpandedStateResult && accordionExpandedStateResult.isAllExpanded === undefined
                ? data && config.isExpandedOnInitialLoad
                : accordionExpandedStateResult && accordionExpandedStateResult.isAllExpanded;
        const viewProps = {
            ...this.props,
            AccordionItemContainer: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, config.className),
                id: `checkout-step-${this.props.config.accordionNumber}`
            },
            AccordionItemContent: {
                className: 'ms-accordion-item-content'
            },
            drawer: {
                tag: ControlledDrawer,
                className: `${this.moduleClassName}__drawer ${this.state.isAccordionSuccess ? 'accordion-success' : ''}`,
                toggleButtonText: (config.accordionItemTitle && config.accordionItemTitle) || '',
                collapseProps: {
                    isOpen:
                        this.state.isAccordionExpanded ||
                        expanded ||
                        // this.props.data.checkoutAccordionStep.result?.currentStep === this.props.config.accordionNumber ||
                        this.shouldAccordionBeOpenedByDefault()
                },
                onToggle: this._onToggle,
                onTextChange: this.handleHeadingChange,
                requestContext: this.props.context.request,
                telemetryContent: this.telemetryContent
            },
            accordionSlots: slots
        };
        return renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.accordionItemTitle = event.target.value);

    private _onToggle(state: IDrawerState): void {
        if (!state.isOpen && this.props.interactionStyle === interactionStyle.ExpandOneItemOnly) {
            const accordionState = { isAllExpanded: false };
            this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
        }
    }

    // function to update state, passed to child
    private setIsAccordionSuccess = (value: boolean) => {
        this.setState({ isAccordionSuccess: value });
    };

    private shouldAccordionBeOpenedByDefault = () => {
        const screenSize = _getScreenSize(this.props.context);
        const cartlines = this.props.data.cart.result?.cart.CartLines;
        let hasHomeDel = false;
        if (this.props.config.accordionNumber === 0 && (screenSize === 3 || screenSize === 4)) {
            return true;
        }
        cartlines?.map(cl => {
            if (!cl.FulfillmentStoreId) {
                hasHomeDel = true;
            }
        });
        if (this.props.data.checkoutAccordionStep.result?.currentStep === 0) {
            if (screenSize === 3 || screenSize === 4) {
                if (hasHomeDel && this.props.config.accordionNumber === 1) {
                    return true;
                } else if (!hasHomeDel && this.props.config.accordionNumber === 2) {
                    return true;
                }
            }
        }
        return false;
    };
}
export default AccordionItem;
